import { useEffect, useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Image,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CloudUploadOutlined,
  OrderedListOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import {
  AnimeHiatusAction,
  isAnimeHiatusActive,
} from '@/components/AnimeHiatusAction'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { useIsMobile } from '@/lib/hooks'
import { formatCST } from '@/lib/time'

export const Route = createFileRoute('/_authed/stale-animes')({
  component: StaleAnimesPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type SortKey = 'stale_desc' | 'stale_asc' | 'title_asc'

interface StaleAnimeRow {
  id: number
  title: string
  title_original: string | null
  cover_url: string | null
  current_episodes: number | null
  total_episodes: number | null
  is_published: boolean | null
  is_finished: boolean | null
  update_weekday: number | null
  view_count: number | null
  hiatus_until: string | null
  last_update_at: string | null
  days_since: number | null
}

interface ListResult {
  ok: boolean
  total?: number
  rows?: Array<StaleAnimeRow>
  cutoff?: string
  days?: number
  error?: string
}

const SORT_LABELS: Record<SortKey, string> = {
  stale_desc: '最久未更新优先',
  stale_asc: '较近未更新优先',
  title_asc: '标题字母序',
}

const WEEKDAY_LABEL = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']

const PAGE_SIZE = 50

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function daysColor(d: number | null): string {
  if (d === null) return 'red'
  if (d > 30) return 'red'
  if (d > 14) return 'orange'
  return 'gold'
}

/* ── 页面 ───────────────────────────────────────────────────── */

function StaleAnimesPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [days, setDays] = useState<number>(8)
  const [sort, setSort] = useState<SortKey>('stale_desc')

  const debouncedSearch = useDebounced(search.trim(), 300)
  const debouncedDays = useDebounced(days, 300)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, debouncedDays, sort])

  const listQuery = useQuery({
    queryKey: [
      'admin_stale_animes',
      { page, debouncedSearch, debouncedDays, sort },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'list_stale_unfinished_animes' as never,
        {
          p_days: debouncedDays,
          p_search: debouncedSearch || null,
          p_sort: sort,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        } as never,
      )
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return {
        rows: r.rows ?? [],
        total: r.total ?? 0,
        cutoff: r.cutoff ?? null,
        days: r.days ?? debouncedDays,
      }
    },
  })

  const columns: ColumnsType<StaleAnimeRow> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 80,
      },
      {
        title: '标题',
        minWidth: 300,
        render: (_: unknown, row) => (
          <Space size={12} align="center">
            {row.cover_url ? (
              <Image
                src={row.cover_url}
                width={40}
                height={56}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                placeholder
                preview={{ mask: false }}
              />
            ) : (
              <div
                className="admin-cell-media"
                style={{
                  width: 40,
                  height: 56,
                  borderRadius: 6,
                  background: 'var(--ant-color-fill-tertiary)',
                }}
              />
            )}
            <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
              <Space size={6} wrap>
                <Link
                  to="/anime/$animeId/episodes"
                  params={{ animeId: String(row.id) }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography.Text strong ellipsis>
                    {row.title}
                  </Typography.Text>
                </Link>
                {isAnimeHiatusActive(row.hiatus_until) ? (
                  <Tag
                    color="warning"
                    title={`停播至 ${formatCST(row.hiatus_until)}`}
                  >
                    停播至 {formatCST(row.hiatus_until, 'MM-DD')}
                  </Tag>
                ) : null}
              </Space>
              {row.title_original ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {row.title_original}
                </Typography.Text>
              ) : null}
            </Space>
          </Space>
        ),
      },
      {
        title: '集数',
        width: 100,
        render: (_: unknown, r) => (
          <Typography.Text>
            {r.current_episodes ?? 0}/{r.total_episodes ?? '?'}
          </Typography.Text>
        ),
      },
      {
        title: '周表',
        dataIndex: 'update_weekday',
        width: 70,
        render: (w: number | null) =>
          w === null ? (
            <Typography.Text type="secondary">—</Typography.Text>
          ) : (
            <Tag>{WEEKDAY_LABEL[w] ?? `?${w}`}</Tag>
          ),
      },
      {
        title: '最后更新',
        dataIndex: 'last_update_at',
        width: 170,
        render: (v: string | null) =>
          v ? (
            <Typography.Text style={{ fontSize: 12 }}>
              {formatCST(v, 'YYYY-MM-DD HH:mm')}
            </Typography.Text>
          ) : (
            <Tag color="red">从未更新</Tag>
          ),
      },
      {
        title: '距今',
        dataIndex: 'days_since',
        width: 100,
        render: (d: number | null, row) =>
          d === null ? (
            <Typography.Text type="secondary">—</Typography.Text>
          ) : (
            /* 停播中的番超期是已知状态，天数降为中性色；异常感只留给没解释的 */
            <Tag
              color={
                isAnimeHiatusActive(row.hiatus_until) ? 'default' : daysColor(d)
              }
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {d} 天
            </Tag>
          ),
      },
      {
        title: '操作',
        width: 230,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={4}>
            <Link
              to="/anime/$animeId/episodes"
              params={{ animeId: String(row.id) }}
              search={{ add: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                type="primary"
                icon={<CloudUploadOutlined />}
              >
                追更
              </Button>
            </Link>
            <Link
              to="/anime/$animeId/episodes"
              params={{ animeId: String(row.id) }}
              onClick={(e) => e.stopPropagation()}
            >
              <Button size="small" icon={<OrderedListOutlined />}>
                剧集
              </Button>
            </Link>
            <AnimeHiatusAction
              animeId={row.id}
              hiatusUntil={row.hiatus_until}
              updateWeekday={row.update_weekday}
              extraInvalidateKeys={[['admin_stale_animes']]}
            />
          </Space>
        ),
      },
    ],
    [],
  )

  const errMsg =
    listQuery.error instanceof Error ? listQuery.error.message : null

  const refreshButton = (
    <Tooltip title={isMobile ? '刷新' : undefined}>
      <Button
        icon={<ReloadOutlined />}
        aria-label="刷新"
        onClick={() => listQuery.refetch()}
        loading={listQuery.isFetching && !listQuery.isLoading}
      >
        {isMobile ? null : '刷新'}
      </Button>
    </Tooltip>
  )
  const searchBox = (
    <Input
      allowClear
      prefix={<SearchOutlined />}
      placeholder="搜标题 / 原名 / 拼音"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      style={{ width: 260 }}
    />
  )
  const daysFilter = (
    <Space size={4} className="stale-days-filter admin-filter-grid__full">
      <Typography.Text>超过</Typography.Text>
      <InputNumber
        min={1}
        max={365}
        value={days}
        onChange={(v) => {
          if (typeof v === 'number' && v >= 1) setDays(v)
        }}
        style={{ width: 80 }}
      />
      <Typography.Text>天未更新</Typography.Text>
    </Space>
  )
  const sortSelect = (
    <Select<SortKey>
      value={sort}
      onChange={setSort}
      style={{ width: 180 }}
      options={Object.entries(SORT_LABELS).map(([value, label]) => ({
        value: value as SortKey,
        label,
      }))}
    />
  )

  const openEpisodes = (row: StaleAnimeRow) =>
    navigate({
      to: '/anime/$animeId/episodes',
      params: { animeId: String(row.id) },
    })

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-header__title">超期未更新番剧</h1>
          <div className="admin-page-header__meta">
            未完结 + 已发布 · 排除 movie/ova/oad/other · 最近一集正片 &gt;{' '}
            {listQuery.data?.days ?? days} 天或从未有正片
          </div>
        </div>
        {/* 手机上刷新挪进筛选栏行尾，页头不再单独占一行 */}
        {isMobile ? null : refreshButton}
      </div>

      <Card className="admin-table-card">
        <div className="admin-table-card__filters">
          {isMobile ? (
            <AdminFilterBar
              primary={searchBox}
              extra={refreshButton}
              activeCount={
                (days !== 8 ? 1 : 0) + (sort !== 'stale_desc' ? 1 : 0)
              }
            >
              {daysFilter}
              {sortSelect}
            </AdminFilterBar>
          ) : (
            <>
              {searchBox}
              {daysFilter}
              {sortSelect}
            </>
          )}
        </div>

        {errMsg ? (
          <Typography.Paragraph type="danger" style={{ margin: '12px 20px 0' }}>
            加载失败：{errMsg}
          </Typography.Paragraph>
        ) : null}

        <Table<StaleAnimeRow>
          className="admin-table-wrap"
          rowKey="id"
          size="small"
          loading={listQuery.isLoading}
          columns={columns}
          dataSource={listQuery.data?.rows ?? []}
          scroll={{ x: 1120 }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: listQuery.data?.total ?? 0,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 部`,
            onChange: (p) => setPage(p),
          }}
        />
        {isMobile ? (
          <div className="stale-mobile">
            <AdminMobileList<StaleAnimeRow>
              rows={listQuery.data?.rows ?? []}
              rowKey={(row) => row.id}
              loading={listQuery.isLoading}
              page={page}
              pageSize={PAGE_SIZE}
              total={listQuery.data?.total ?? 0}
              onPageChange={setPage}
              renderItem={(row) => {
                const hiatus = isAnimeHiatusActive(row.hiatus_until)
                return (
                  <article
                    className="admin-mobile-record anime-mobile-card"
                    role="button"
                    tabIndex={0}
                    aria-label={`管理《${row.title}》剧集`}
                    onClick={() => openEpisodes(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openEpisodes(row)
                      }
                    }}
                  >
                    <div className="admin-mobile-record__lead">
                      {row.cover_url ? (
                        <img
                          className="anime-mobile-card__cover"
                          src={row.cover_url}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        <div className="anime-mobile-card__cover" />
                      )}
                      <div className="anime-mobile-card__main">
                        <div className="admin-mobile-record__head">
                          <div className="admin-mobile-record__title anime-mobile-card__title">
                            {row.title}
                          </div>
                          {row.days_since === null ? (
                            <Tag color="red" style={{ marginInlineEnd: 0 }}>
                              从未更新
                            </Tag>
                          ) : (
                            <Tag
                              color={
                                hiatus ? 'default' : daysColor(row.days_since)
                              }
                              style={{
                                marginInlineEnd: 0,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {row.days_since} 天
                            </Tag>
                          )}
                        </div>
                        {row.title_original ? (
                          <div className="anime-mobile-card__sub">
                            {row.title_original}
                          </div>
                        ) : null}
                        <div className="admin-mobile-record__meta">
                          <span className="anime-mobile-card__num">
                            {row.current_episodes ?? 0}/
                            {row.total_episodes ?? '?'} 集
                          </span>
                          {row.update_weekday !== null ? (
                            <span>
                              {WEEKDAY_LABEL[row.update_weekday] ??
                                `?${row.update_weekday}`}
                              更新
                            </span>
                          ) : null}
                          {row.last_update_at ? (
                            <span>
                              最后{' '}
                              {formatCST(row.last_update_at, 'MM-DD HH:mm')}
                            </span>
                          ) : null}
                        </div>
                        {hiatus ? (
                          <div className="admin-mobile-record__badges">
                            <Tag color="warning">
                              停播至 {formatCST(row.hiatus_until, 'MM-DD')}
                            </Tag>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className="admin-mobile-record__actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <span className="admin-mobile-record__leading anime-mobile-card__num">
                        #{row.id}
                      </span>
                      <AnimeHiatusAction
                        animeId={row.id}
                        hiatusUntil={row.hiatus_until}
                        updateWeekday={row.update_weekday}
                        extraInvalidateKeys={[['admin_stale_animes']]}
                        buttonType="default"
                      />
                      <Button
                        type="primary"
                        icon={<CloudUploadOutlined />}
                        onClick={() =>
                          navigate({
                            to: '/anime/$animeId/episodes',
                            params: { animeId: String(row.id) },
                            search: { add: 1 },
                          })
                        }
                      >
                        追更
                      </Button>
                    </div>
                  </article>
                )
              }}
            />
          </div>
        ) : null}
      </Card>
    </div>
  )
}
